import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Autocomplete,AutocompleteChangeReason,Button,Pagination,Skeleton,Stack,TextField,Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useQuery } from '@tanstack/react-query';
import React,{ useContext,useEffect,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UserContext } from '../../context/UserContext';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import Clients,{ ClientType } from '../../service/Clients';
import Places,{ PlaceType } from '../../service/Places';
import usePagination from '../Pagination/Pagination';
import NewPlaceModal from './Modal/NewPlaceModal';
import PlacesAcordeon from './PlacesAcordeon';

const clientsService = new Clients();
const placesService = new Places();

export type reorderType = {
  [key: string]: string
};

export default function PlacesComponent() {
  const { customerId, setCustomerId } = useContext(UserContext);
  const [selected, setSelected] = useState(['initial']);
  const { hierarchyLabel } = useContext(UserContext);
  const [search, setSearch] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [paginated, setPaginated] = useState<PlaceType[]>([]);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [reorderBy, setReorderBy] = useState<string>('createBy');
  const [local, setLocal] = useState<reorderType>({});
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);

  const handleChange = (isExpanded: boolean, panel: string) => {
    setExpanded(isExpanded ? panel : false);
  };

  const { t } = useTranslation('translation');

  const { data, isLoading: loadingClientes, isFetching: isFetchingClientes, isSuccess: clientsLoaded, isError: clientsError } = useQuery({
    queryKey: ['GET_CLIENTES'],
    queryFn: async () => {
      const token = await AttToken();
      if (!token) {
        throw new Error('Token not found');
      }
      return toast.promise(
        clientsService.getAllClients(token, false),
        {
          pending: `${t('places.buscando')} ${hierarchyLabel.cliente.toLocaleLowerCase()}...`,
          success: {
            render({ data }) {
              if (!data?.clientList) {
                throw new Error('Invalid response from server');
              }
              return `${hierarchyLabel.cliente} ${t('places.sucessoLocais')}`;
            },
          },
          error: `${t('places.erroBuscar')} ${hierarchyLabel.cliente.toLocaleLowerCase()}`
        }, { toastId: 'buscaClientes' },
      ).then((response) => {
        if (!response?.clientList) {
          throw new Error('Invalid response from server');
        }
        return response.clientList;
      });
    },
    retry: 3,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });

  const { data: placesData, isLoading, isFetching, isSuccess, isError: placesError } = useQuery({
    queryKey: ['GET_PLACES', customerId],
    queryFn: async () => {
      const token = await AttToken();

      if (!token) {
        throw new Error('Token not found');
      }
      return toast.promise(
        placesService.getPlacesWithChildren(token, customerId),
        {
          pending: `${t('places.buscando')} ${hierarchyLabel.local.toLocaleLowerCase()}...`,
          success: `${hierarchyLabel.local} ${t('places.sucessoLocais')}`,
          error: `${t('places.erroBuscar')} ${hierarchyLabel.local.toLocaleLowerCase()}`
        }, { toastId: 'buscaLocais' }
      ).then((response) => {
        if (!response) {
          throw new Error('Invalid response from server');
        }
        return response;
      });
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
    enabled: customerId?.length > 0,
  });

  const onLoadingClientes = loadingClientes || isFetchingClientes;

  const onLoading = isLoading || isFetching;

  useEffect(() => {
    if (clientsLoaded) trackEventMatomo('gestão de locais', 'sucesso', 'carregamento', 'clientes carregados');
    if (clientsError) trackEventMatomo('gestão de locais', 'erro', 'carregamento', 'falha ao carregar clientes');
  }, [clientsError, clientsLoaded]);

  useEffect(() => {
    if (isSuccess) trackEventMatomo('gestão de locais', 'sucesso', 'carregamento', 'locais carregados');
    if (placesError) trackEventMatomo('gestão de locais', 'erro', 'carregamento', 'falha ao carregar locais');
  }, [isSuccess, placesError]);

  const lastPageIndex = currentPage * itemsPerPage;
  const firstPageIndex = lastPageIndex - itemsPerPage;

  const switchSort = (value: string, a: PlaceType, b: PlaceType) => {
    switch (value) {
      case 'createBy':
        return (a.dataCadastro && b.dataCadastro) && (a?.dataCadastro < b?.dataCadastro);
      case 'crescent':
        return (a.nome.toLowerCase() > b.nome.toLowerCase());
      case 'decrescent':
        return (a.nome.toLowerCase() < b.nome.toLowerCase());
      default: return (a.dataCadastro && b.dataCadastro) && (a?.dataCadastro < b?.dataCadastro);
    }
  };

  useEffect(() => {
    if (isSuccess) {
      const currentPageData = placesData.locais?.sort((a: PlaceType, b: PlaceType) => switchSort(reorderBy, a, b) ? 1 : -1)
        ?.filter((e: PlaceType) => e.nome.toLowerCase()?.includes(search?.toLowerCase()));
      setPaginated(currentPageData?.slice(firstPageIndex, lastPageIndex));
    }
  }, [firstPageIndex, lastPageIndex, reorderBy, isSuccess, placesData, search]);

  const pages = [];

  for (let i = 1; i <= Math.ceil(placesData?.locais?.length / itemsPerPage); i++) {
    pages.push(i);
  }

  const PER_PAGE = 5;
  const count = Math.ceil(placesData?.locais?.length / PER_PAGE);
  const _DATA = usePagination<PlaceType>(placesData?.locais?.sort((a: PlaceType, b: PlaceType) => { return a.nome > b.nome ? 1 : -1; }), PER_PAGE);

  useEffect(() => {
    setPaginated(_DATA.currentData());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  function Loading() {
    return (
      <>
        <Skeleton variant='rounded' height={50} width='100%' />
        <Skeleton variant='rounded' width={250} height={40} />
        {Array.from(new Array(itemsPerPage)).map((_, index) => (
          <Skeleton key={index} variant='rounded' width={'100%'} height={80} />
        ))}
      </>);
  };

  const handleChangePage = (_e: React.SyntheticEvent<unknown>, p: number) => {
    trackEventMatomo('gestão de locais', 'click', 'paginação', `acessa página ${p}`);
    setPage(p);
    _DATA.jump(p);
  };

  return (
    <>
      {open &&
        <NewPlaceModal
          open={open}
          setOpen={setOpen}
          customerId={customerId} />}
      <Stack direction='column' justifyContent='flex-start' alignItems='flex-start' spacing={2} width='100%' paddingBottom={2} paddingTop={2}>
        <Autocomplete
          fullWidth
          onClick={() => trackEventMatomo('tela de locais', 'click', 'select', 'seleciona valor')}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          filterSelectedOptions
          loading={onLoadingClientes}
          disabled={onLoadingClientes}
          options={
            data
              ?.sort((a: ClientType, b: ClientType) => { return a.nome.toLowerCase() > b.nome.toLowerCase() ? 1 : - 1; })
              ?.filter((e: ClientType) => e.status === 'ativo')
              ?.map((ele: ClientType) => ({
                label: ele.nome,
                value: ele.id,
              })) || []}
          onChange={(event: React.SyntheticEvent, newValue: { label: string, value: string } | null, reason: AutocompleteChangeReason) => {
            if (newValue) trackEventMatomo('gestão de locais', 'click', 'select', 'seleciona cliente');
            if (reason === 'clear') {
              setCustomerId('');
            }
            handleChangePage(event, 1);
            setCustomerId(newValue?.value);
          }}
          renderOption={(props, option: { label: string, value: string },) => (
            <li {...props} key={option.value}>
              <span>{option.label}</span>
            </li>)}
          renderInput={(params) => <TextField {...params} label={`${t('Services.selectCustomer')} ${hierarchyLabel.cliente}`} />}
        />
        {typeof customerId === 'string' && customerId?.length > 0
          && <>
            <Button
              variant='contained'
              color='success'
              endIcon={<AddCircleIcon />}
              onClick={() => {
                trackEventMatomo('tela de locais', 'click', 'button', 'abre modal');
                setOpen(true);
              }}
              disabled={customerId === '' || onLoading}
            >
              {`${t('add')} ${hierarchyLabel.local ?? 'Local'}`}
            </Button>
            {onLoading
              ? <Loading />
              : <> {pages?.length > 1
                && (<>
                  <Pagination
                    disabled={search.length > 0}
                    boundaryCount={4}
                    showFirstButton
                    showLastButton
                    count={count}
                    size="small"
                    page={page}
                    variant='text'
                    shape='circular'
                    onChange={handleChangePage}
                  />
                  <Autocomplete
                    fullWidth
                    disablePortal
                    disabled={onLoading}
                    loading={onLoading}
                    options={placesData?.locais?.map((local: PlaceType) => ({
                      ...local
                    })) ?? []}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => option.nome}
                    filterSelectedOptions
                    onChange={(event: React.SyntheticEvent, value: { nome: string, id: string } | null, reason: AutocompleteChangeReason) => {
                      if (value) trackEventMatomo('gestão de locais', 'click', 'select', 'pesquisa local');
                      handleChangePage(event, 1);
                      setSearch(value?.nome as string);
                      if (reason === 'clear') {
                        setSearch('');
                      }
                    }}
                    renderOption={(props, option,) => (
                      <li {...props} key={option.id}>
                        <span>{option.nome}</span>
                      </li>)}
                    renderInput={(params) => <TextField {...params} label={`${t('search')} ${hierarchyLabel.local}`} />}
                  />
                </>)}
                {placesData?.locais.length > 0 ? <>
                  <FormControl sx={{ width: '300px' }}>
                    <InputLabel>{t('Services.servicesManagement.sortBy')}</InputLabel>
                    <Select
                      value={reorderBy ?? ''}
                      label={t('Services.servicesManagement.sortBy')}
                      onChange={(event) => {
                        trackEventMatomo('gestão de locais', 'click', 'select', 'altera ordenação de locais');
                        setReorderBy(event.target.value);
                        local[customerId] = event.target.value;
                        setLocal(local);
                        localStorage.setItem('localOrder', JSON.stringify(local));
                      }}
                    >
                      <MenuItem value={'createBy'}>{t('Services.servicesManagement.byCreationDate')}</MenuItem>
                      <MenuItem value={'crescent'}>{t('Services.servicesManagement.alphabeticalAscending')} (A-Z)</MenuItem>
                      <MenuItem value={'decrescent'}>{t('Services.servicesManagement.alphabeticalDescending')} (Z-A)</MenuItem>
                    </Select>
                  </FormControl>
                  {paginated?.map((e: PlaceType) => (
                    <PlacesAcordeon
                      setCurrentPage={setCurrentPage}
                      local={local}
                      setLocal={setLocal}
                      expanded={expanded}
                      handleChange={handleChange}
                      place={e}
                      key={e.id}
                      selected={selected}
                      setSelected={setSelected}
                    />
                  ))}
                </> : <Typography textAlign='center' color='textDisabled' variant='h4'>
                  Você ainda não criou nenhum local, clique no botão "ADICIONAR LOCAL +" para criar o primeiro</Typography>}
              </>}
          </>
        }
      </Stack>
    </ >
  );
} 